import { useEffect, useState } from 'react';
import { matchPath, type NavigateFunction } from 'react-router-dom';

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';

import debounce from 'lodash.debounce';

import {
  PushNotificationId,
  PushNotificationToken,
  PushNotificationTokenStatus,
  Token,
  Workspace,
} from '../constants/keys';
import { PathnamesMap, ValidRoutes } from '../constants/ui';
import { useCreateTokenMutation, useUpdateTokenMutation } from '../store/api/notifications';
import type { NotificationPlatform } from '../types/notifications';
import { useMobileStorage } from './useMobileStorage';
import { useLocalStorage } from './useLocalStorage';

interface UsePushNotificationsProps {
  navigate: NavigateFunction;
}

const usePushNotifications = async ({ navigate }: UsePushNotificationsProps) => {
  const accessToken = localStorage.getItem(Token);

  const [pnToken] = useLocalStorage(PushNotificationToken, '');
  const [, setPnId] = useMobileStorage(PushNotificationId, '');
  const [pnStatus, setPnStatus] = useMobileStorage(PushNotificationTokenStatus, '');

  const [pnTokenState] = useState(pnToken);

  const [
    createToken,
    { data: createTokenData, isSuccess: isCreateTokenSuccess, isError: isCreateTokenError, error: createTokenError },
  ] = useCreateTokenMutation();
  const [
    updateToken,
    { data: updateTokenData, isSuccess: isUpdateTokenSuccess, isError: isUpdateTokenError, error: updateTokenError },
  ] = useUpdateTokenMutation();

  // Resend token to backend
  const resendToken = async () => {
    const token = await Preferences.get({ key: PushNotificationToken });

    if (token.value) {
      setPnStatus('');
      await sendPushNotificationToken(token.value);
    }
  };

  // Send token to backend
  const sendPushNotificationToken = debounce(
    async (tokenValue: string) => {
      const status = await Preferences.get({ key: PushNotificationTokenStatus });
      const id = await Preferences.get({ key: PushNotificationId });

      const statusRef = status.value ? status.value.replace(/"/g, '') : '';
      const idRef = id.value ? id.value.replace(/"/g, '') : '';

      if (statusRef === 'updated' || statusRef === 'error' || statusRef === 'loading' || !tokenValue) return;

      const platform = Capacitor.getPlatform() as NotificationPlatform;
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (!idRef || statusRef === 'removed') {
        createToken({ token: tokenValue, platform, timezone: currentTimezone });
      } else {
        updateToken({ id: idRef, token: tokenValue, platform, timezone: currentTimezone });
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setup = async () => {
      // Permissions
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      if (permStatus.receive !== 'granted') {
        console.log('User denied permissions!');
      }
      await PushNotifications.register();

      // Registration
      PushNotifications.addListener('registration', async (token) => {
        console.log('Push token:', token.value);

        if (!pnTokenState || token.value !== pnTokenState || !pnStatus) {
          await Preferences.set({
            key: PushNotificationToken,
            value: token.value,
          });
          await sendPushNotificationToken(token.value);
        }
      }).then();

      // Registration error
      PushNotifications.addListener('registrationError', (err) => {
        console.error('Registration error:', err);
      }).then();

      // Notification clicked by the user
      await PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
        console.log('Push notification', notification);

        const url = notification.notification.data?.url;
        const workspace = notification.notification.data?.workspace_id;
        const isExternal = /^https?:\/\//i.test(url);

        if (isExternal) {
          await Browser.open({ url });
        } else {
          const isValidRoute = ValidRoutes.some((route) => matchPath({ path: route, end: false }, url));
          if (isValidRoute) {
            // Select workspace
            if (workspace) {
              localStorage.setItem(Workspace, JSON.stringify(workspace));
              setTimeout(() => {
                window.location.href = url;
              }, 1000);
            } else {
              navigate(url);
            }
          } else {
            // Internal URL is invalid or 404, redirecting to dashboard
            navigate(`/${PathnamesMap.dashboard}`);
          }
        }
      });
    };

    setup().then();
  }, []);

  useEffect(() => {
    if (!isCreateTokenSuccess || !createTokenData) return;
    console.log('====Token ID====', createTokenData.id);

    if (createTokenData.id) {
      setPnId(createTokenData.id);
      setPnStatus('updated');
    }
  }, [isCreateTokenSuccess, createTokenData]);

  useEffect(() => {
    if (!isUpdateTokenSuccess || !updateTokenData) return;
    console.log('====Token ID (Update)====', updateTokenData.id);

    if (updateTokenData.id) {
      setPnId(updateTokenData.id);
      setPnStatus('updated');
    }
  }, [isUpdateTokenSuccess, updateTokenData]);

  useEffect(() => {
    if (!isCreateTokenError && !isUpdateTokenError) return;
    let code: number | string | undefined;

    if (isCreateTokenError && createTokenError && 'status' in createTokenError) {
      // console.log('======Token Error=======', createTokenError, createTokenError.data.errors[0].detail);
      code = createTokenError.status;
    }
    if (isUpdateTokenError && updateTokenError && 'status' in updateTokenError) {
      // console.log('======Token Update Error=======', updateTokenError, updateTokenError.data.errors[0].detail);
      code = updateTokenError.status;
    }

    if (typeof code === 'number' && code > 399 && code < 499) {
      setPnId('');
      setPnStatus('removed');
      resendToken().then();
    } else {
      setPnStatus('error');
    }
  }, [isCreateTokenError, isUpdateTokenError]);

  // Resend token after login/logout
  useEffect(() => {
    resendToken();
  }, [accessToken]);

  return { resendToken };
};

export default usePushNotifications;
